import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { cn, formatCurrency } from "@/lib/utils";
import { toast } from "sonner";
import {
  ChevronLeft,
  Loader2,
  FileText,
  Stamp,
  ExternalLink,
  Download,
} from "lucide-react";

// ─── Helpers ────────────────────────────────────────
function formatDate(dateStr) {
  if (!dateStr) return "-";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  const months = ["Ocak","Şubat","Mart","Nisan","Mayıs","Haziran","Temmuz","Ağustos","Eylül","Ekim","Kasım","Aralık"];
  return `${String(d.getDate()).padStart(2, "0")} ${months[d.getMonth()]} ${d.getFullYear()}`;
}

function statusBadge(status) {
  const map = {
    paid: { label: "Resmileştirildi", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
    active: { label: "Aktif", cls: "bg-sky-50 text-sky-700 border-sky-200" },
    draft: { label: "Taslak", cls: "bg-muted/60 text-muted-foreground border-border" },
    archived: { label: "Arşivlendi", cls: "bg-muted/60 text-muted-foreground border-border" },
    overdue: { label: "Gecikmiş", cls: "bg-rose-50 text-rose-700 border-rose-200" },
  };
  const s = map[status] || { label: status || "-", cls: "bg-muted/60 text-muted-foreground border-border" };
  return (
    <span className={cn("inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium border", s.cls)} data-testid="invoice-status-badge">
      {s.label}
    </span>
  );
}

function fmtNum(val) {
  if (val == null) return "0,00";
  const n = parseFloat(val);
  if (isNaN(n)) return val;
  return n.toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 6 });
}

function normalizeCurrencyLabel(currency) {
  const code = String(currency || "").trim().toUpperCase();
  if (code === "TRY" || code === "TRL") return "₺";
  return code || "-";
}

function toNum(val, fallback = 0) {
  if (val == null || val === "") return fallback;
  if (typeof val === "number") return Number.isFinite(val) ? val : fallback;
  let s = String(val).trim();
  if (s.includes(",") && s.includes(".")) {
    // Locale format like 1.234,56
    if (s.lastIndexOf(",") > s.lastIndexOf(".")) {
      s = s.replace(/\./g, "").replace(",", ".");
    } else {
      // Locale format like 1,234.56
      s = s.replace(/,/g, "");
    }
  } else if (s.includes(",")) {
    // Decimal comma
    s = s.replace(",", ".");
  }
  const parsed = Number(s);
  return Number.isFinite(parsed) ? parsed : fallback;
}

// ─── Main Component ──────────────────────────────
export default function InvoiceDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [officializing, setOfficializing] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  const loadInvoice = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.get(`/parasut/invoices/${id}`);
      setInvoice(data);
    } catch (err) {
      setInvoice(null);
      const status = Number(err?.status || 0);
      const msg = err?.message || "Fatura yüklenemedi";
      if (status === 404) {
        setError("Fatura bulunamadı");
      } else {
        setError(msg);
        toast.error("Fatura yüklenirken hata oluştu");
      }
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadInvoice();
  }, [loadInvoice]);

  const handleOpenInParasut = () => {
    if (!invoice?.parasut_url) {
      toast.error("Paraşüt bağlantısı bulunamadı");
      return;
    }
    window.open(invoice.parasut_url, "_blank", "noopener,noreferrer");
  };

  const handleDownloadPdf = async () => {
    setDownloadingPdf(true);
    try {
      const res = await api.get(`/parasut/invoices/${id}/pdf`);
      if (res?.file_url) {
        const a = document.createElement("a");
        a.href = res.file_url;
        a.target = "_blank";
        a.rel = "noopener noreferrer";
        a.download = res.filename || `fatura-${id}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        return;
      }
      if (!res?.data_base64) {
        throw new Error("PDF verisi alınamadı");
      }
      const byteChars = atob(res.data_base64);
      const byteNumbers = new Array(byteChars.length);
      for (let i = 0; i < byteChars.length; i++) byteNumbers[i] = byteChars.charCodeAt(i);
      const blob = new Blob([new Uint8Array(byteNumbers)], { type: res.mime || "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = res.filename || `fatura-${id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err.message || "PDF indirilemedi");
    } finally {
      setDownloadingPdf(false);
    }
  };

  const handleOfficialize = async () => {
    setOfficializing(true);
    try {
      const res = await api.post(`/parasut/invoices/${id}/officialize`);
      toast.success(res?.message || "Resmileştirme işlemi başlatıldı");
      await loadInvoice();
    } catch (err) {
      toast.error(err.message || "Resmileştirme işlemi başarısız");
    } finally {
      setOfficializing(false);
    }
  };

  if (loading) {
    return (
      <div className="h-[calc(100dvh-4rem)] flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        <span className="ml-2 text-sm text-muted-foreground">Fatura yükleniyor...</span>
      </div>
    );
  }

  if (error || !invoice) {
    return (
      <div className="h-[calc(100dvh-4rem)] flex items-center justify-center p-6">
        <div className="text-center max-w-md">
          <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
          <h2 className="text-base font-semibold text-foreground mb-2">Fatura bulunamadı</h2>
          <p className="text-sm text-muted-foreground mb-4">{error}</p>
          <button
            onClick={() => navigate("/panel/satislar/faturalar")}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-muted/60 text-foreground/80 text-sm font-medium hover:bg-muted transition"
          >
            <ChevronLeft className="h-4 w-4" />
            Faturalara Dön
          </button>
        </div>
      </div>
    );
  }

  const details = invoice.details || [];
  const displayGrossTotal = toNum(invoice.display_gross_total, 0); // Genel Toplam (net_total)
  const attrGrossTotal = toNum(invoice.gross_total, 0); // Ara Toplam (KDV hariç)
  const attrVat = Math.max(toNum(invoice.total_vat, 0), 0);
  const fallbackGeneral = attrGrossTotal + attrVat;
  const generalTotal = displayGrossTotal || fallbackGeneral;

  const normalizedLinesBase = details.map((item) => {
    const quantity = Math.max(toNum(item.quantity, 1), 0);
    const vatRate = Math.max(toNum(item.vat_rate, 0), 0);
    const rawNet = Math.max(toNum(item.net_total, 0), 0);
    const unitPrice = Math.max(toNum(item.unit_price, 0), 0);
    const computedNet = quantity > 0 && unitPrice > 0 ? quantity * unitPrice : 0;
    const lineNet = rawNet > 0 ? rawNet : computedNet;
    const lineVat = lineNet * (vatRate / 100);
    const lineGross = lineNet + lineVat;
    return { item, quantity, vatRate, lineNet, lineVat, lineGross };
  });

  const sumLineNet = normalizedLinesBase.reduce((acc, l) => acc + l.lineNet, 0);
  const sumLineGross = normalizedLinesBase.reduce((acc, l) => acc + l.lineGross, 0);
  const lineNetAlreadyGross =
    generalTotal > 0 &&
    normalizedLinesBase.length > 0 &&
    Math.abs(sumLineNet - generalTotal) + 0.01 < Math.abs(sumLineGross - generalTotal);

  let normalizedLines = normalizedLinesBase.map((line) => {
    if (!lineNetAlreadyGross) return line;
    const divider = 1 + line.vatRate / 100;
    const adjustedNet = divider > 0 ? line.lineNet / divider : line.lineNet;
    const adjustedVat = line.lineNet - adjustedNet;
    return {
      ...line,
      lineNet: adjustedNet,
      lineVat: adjustedVat,
      lineGross: line.lineNet,
    };
  });

  // Summary mapping (Yengeç parity):
  // Ürün / Hizmet Toplamı = gross_total
  // Genel Toplam = net_total (displayGrossTotal)
  // Toplam KDV = Genel Toplam - Ürün/Hizmet Toplamı
  let netTotal = attrGrossTotal > 0 ? attrGrossTotal : Math.max(generalTotal - attrVat, 0);
  let totalVat = Math.max(generalTotal - netTotal, 0);

  if (netTotal <= 0 && normalizedLines.length > 0) {
    netTotal = normalizedLines.reduce((acc, l) => acc + l.lineNet, 0);
    totalVat = Math.max(generalTotal - netTotal, 0);
  }

  const firstRate = normalizedLines[0]?.vatRate || 0;
  const vatGroups = { [Number(firstRate).toFixed(2)]: totalVat };

  return (
    <div className="page-shell min-h-[calc(100dvh-4rem)] overflow-x-hidden" data-testid="invoice-detail-page">
      <div className="surface-card overflow-hidden">
      {/* ── Header ── */}
      <div className="flex-none border-b border-border bg-card px-4 md:px-6 py-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate("/panel/satislar/faturalar")}
              className="h-9 w-9 rounded-xl border border-border bg-card inline-flex items-center justify-center text-muted-foreground hover:bg-muted/50 transition"
              data-testid="back-to-invoices"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <div>
              <h1 className="text-base font-semibold text-foreground" data-testid="invoice-detail-title">
                {invoice.description || `Fatura #${invoice.id}`}
              </h1>
              <div className="flex items-center gap-2 mt-0.5">
                {invoice.invoice_no && (
                  <span className="text-xs text-muted-foreground" data-testid="invoice-detail-no">{invoice.invoice_no}</span>
                )}
                {statusBadge(invoice.status)}
              </div>
            </div>
          </div>
          {/* Actions */}
          <div className="grid w-full grid-cols-1 gap-2 sm:w-auto sm:flex sm:items-center sm:gap-2">
            <button
              className="inline-flex w-full items-center justify-center gap-2 px-4 py-2.5 rounded-xl border border-border bg-card text-foreground/80 text-sm font-medium hover:bg-muted/50 transition-colors sm:w-auto"
              data-testid="open-in-parasut-btn"
              onClick={handleOpenInParasut}
              disabled={!invoice.parasut_url}
            >
              <ExternalLink className="h-4 w-4" />
              Paraşüt'te Görüntüle
            </button>
            {!invoice.can_officialize && !!invoice.invoice_no && (
              <button
                className="inline-flex w-full items-center justify-center gap-2 px-4 py-2.5 rounded-xl border border-border bg-card text-foreground/80 text-sm font-medium hover:bg-muted/50 transition-colors disabled:opacity-60 sm:w-auto"
                data-testid="download-pdf-btn"
                onClick={handleDownloadPdf}
                disabled={downloadingPdf}
              >
                {downloadingPdf ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                PDF İndir
              </button>
            )}
            <button
              className="inline-flex w-full items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-[#1487a3] text-white text-sm font-medium hover:bg-[#1e7ab3] transition-colors shadow-sm disabled:opacity-60 sm:w-auto"
              data-testid="officialize-invoice-btn"
              onClick={handleOfficialize}
              disabled={!invoice.can_officialize || officializing}
            >
              {officializing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Stamp className="h-4 w-4" />}
              {invoice.can_officialize ? "Resmileştir" : "Resmileştirildi"}
            </button>
          </div>
        </div>
      </div>

      {/* ── Content ── */}
      <div className="flex-1 overflow-auto bg-muted/50/40 p-3 sm:p-4 md:p-6">
        <div className="max-w-6xl mx-auto space-y-5">

          {/* ── Invoice Info Cards ── */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Fatura Bilgileri */}
            <div className="bg-card rounded-2xl border border-border/80 p-5" data-testid="invoice-info-card">
              <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Fatura Bilgileri</h3>
              <dl className="space-y-2 text-sm">
                <div className="flex items-start justify-between gap-3">
                  <dt className="text-muted-foreground">Fatura No</dt>
                  <dd className="font-medium text-foreground text-right break-words">{invoice.invoice_no || "-"}</dd>
                </div>
                <div className="flex items-start justify-between gap-3">
                  <dt className="text-muted-foreground">Fatura Tarihi</dt>
                  <dd className="text-foreground/80 text-right">{formatDate(invoice.issue_date)}</dd>
                </div>
                <div className="flex items-start justify-between gap-3">
                  <dt className="text-muted-foreground">Vade Tarihi</dt>
                  <dd className="text-foreground/80 text-right">{formatDate(invoice.due_date)}</dd>
                </div>
                <div className="flex items-start justify-between gap-3">
                  <dt className="text-muted-foreground">Durum</dt>
                  <dd>{statusBadge(invoice.status)}</dd>
                </div>
              </dl>
            </div>

            {/* Sipariş Bilgileri */}
            <div className="bg-card rounded-2xl border border-border/80 p-5" data-testid="order-info-card">
              <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Sipariş Bilgileri</h3>
              <dl className="space-y-2 text-sm">
                <div className="flex items-start justify-between gap-3">
                  <dt className="text-muted-foreground">Sipariş No</dt>
                  <dd className="font-medium text-foreground text-right break-words">{invoice.order_no || "-"}</dd>
                </div>
                <div className="flex items-start justify-between gap-3">
                  <dt className="text-muted-foreground">Sipariş Tarihi</dt>
                  <dd className="text-foreground/80 text-right">{formatDate(invoice.order_date)}</dd>
                </div>
                <div className="flex items-start justify-between gap-3">
                  <dt className="text-muted-foreground">Sevk Tarihi</dt>
                  <dd className="text-foreground/80 text-right">{formatDate(invoice.shipment_date)}</dd>
                </div>
                <div className="flex items-start justify-between gap-3">
                  <dt className="text-muted-foreground">Para Birimi</dt>
                  <dd className="text-foreground/80 text-right">{normalizeCurrencyLabel(invoice.currency)}</dd>
                </div>
              </dl>
            </div>

            {/* Müşteri Bilgileri */}
            <div className="bg-card rounded-2xl border border-border/80 p-5" data-testid="contact-info-card">
              <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Müşteri Bilgileri</h3>
              {invoice.contact ? (
                <dl className="space-y-2 text-sm">
                  <div className="flex items-start justify-between gap-3">
                    <dt className="text-muted-foreground">Ad</dt>
                    <dd className="font-medium text-foreground text-right break-words max-w-[180px]">{invoice.contact.name || "-"}</dd>
                  </div>
                  {invoice.contact.tax_office && (
                    <div className="flex items-start justify-between gap-3">
                      <dt className="text-muted-foreground">Vergi Dairesi</dt>
                      <dd className="text-foreground/80 text-right break-words max-w-[180px]">{invoice.contact.tax_office}</dd>
                    </div>
                  )}
                  {invoice.contact.tax_number && (
                    <div className="flex items-start justify-between gap-3">
                      <dt className="text-muted-foreground">VKN / TCKN</dt>
                      <dd className="text-foreground/80 text-right break-words">{invoice.contact.tax_number}</dd>
                    </div>
                  )}
                  {invoice.contact.city && (
                    <div className="flex items-start justify-between gap-3">
                      <dt className="text-muted-foreground">Şehir</dt>
                      <dd className="text-foreground/80 text-right break-words">{invoice.contact.city}{invoice.contact.district ? ` / ${invoice.contact.district}` : ""}</dd>
                    </div>
                  )}
                </dl>
              ) : (
                <p className="text-sm text-muted-foreground">Müşteri bilgisi bulunamadı</p>
              )}
            </div>
          </div>

          {/* ── Product/Service Table (matches Yengeç) ── */}
          <div className="bg-card rounded-2xl border border-border/80 overflow-hidden" data-testid="invoice-items-table">
            <div className="overflow-x-auto">
            <table className="w-full min-w-[760px] text-sm">
              <thead className="bg-muted/30 border-b border-border">
                <tr>
                  <th className="px-5 py-3 text-left text-[10.5px] font-bold uppercase tracking-[0.07em] text-muted-foreground">Ürün / Hizmet</th>
                  <th className="px-4 py-3 text-center text-[10.5px] font-bold uppercase tracking-[0.07em] text-muted-foreground">Miktar</th>
                  <th className="px-4 py-3 text-right text-[10.5px] font-bold uppercase tracking-[0.07em] text-muted-foreground">Birim Fiyatı</th>
                  <th className="px-4 py-3 text-right text-[10.5px] font-bold uppercase tracking-[0.07em] text-muted-foreground">İndirim</th>
                  <th className="px-4 py-3 text-center text-[10.5px] font-bold uppercase tracking-[0.07em] text-muted-foreground">Vergi</th>
                  <th className="px-4 py-3 text-right text-[10.5px] font-bold uppercase tracking-[0.07em] text-muted-foreground">Toplam</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {normalizedLines.length > 0 ? normalizedLines.map((line, idx) => {
                  const item = line.item;
                  return (
                  <tr key={item.id || idx} className="hover:bg-muted/40 transition-colors" data-testid={`invoice-item-${idx}`}>
                    <td className="px-5 py-4">
                      <div className="font-medium text-foreground">{item.product_name}</div>
                      {item.product_code && (
                        <div className="mt-1 inline-flex items-center rounded-full border border-border bg-muted/50 px-2.5 py-0.5 text-[11px] text-muted-foreground">
                          Stok Kodu: {item.product_code}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-4 text-center text-foreground/80 whitespace-nowrap">
                      {line.quantity} {item.unit || "Adet"}
                    </td>
                    <td className="px-4 py-4 text-right text-foreground/80 whitespace-nowrap">
                      {fmtNum(item.unit_price)} ₺
                    </td>
                    <td className="px-4 py-4 text-right text-foreground/80 whitespace-nowrap">
                      {fmtNum(item.discount)} {item.discount_type === "percentage" ? "%" : "₺"}
                    </td>
                    <td className="px-4 py-4 text-center text-foreground/80 whitespace-nowrap">
                      %{Number(line.vatRate || 0).toLocaleString("tr-TR", { maximumFractionDigits: 2 })}
                    </td>
                    <td className="px-4 py-4 text-right font-medium text-foreground whitespace-nowrap">
                      {fmtNum(line.lineGross)} ₺
                    </td>
                  </tr>
                )}) : (
                  <tr>
                    <td colSpan={6} className="px-5 py-8 text-center text-muted-foreground">
                      Fatura kalemi bulunamadı
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            </div>

            {/* ── Totals Section (matches Yengeç layout) ── */}
            <div className="border-t border-border bg-muted/50/50">
              <div className="max-w-sm ml-auto px-5 py-4">
                <div className="rounded-2xl border border-border bg-card px-4 py-3 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Ürün / Hizmet Toplamı</span>
                  <span className="font-medium text-slate-800">{fmtNum(netTotal)} ₺</span>
                </div>

                {Object.entries(vatGroups).map(([rate, amount]) => (
                  <div key={rate} className="flex justify-between text-sm">
                    <span className="text-muted-foreground">
                      Toplam KDV (%{Number(rate || 0).toLocaleString("tr-TR", { maximumFractionDigits: 2 })})
                    </span>
                    <span className="text-foreground/80">{fmtNum(amount)} ₺</span>
                  </div>
                ))}

                <div className="border-t border-border my-2" />

                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Brüt Toplam</span>
                  <span className="font-medium text-slate-800">{fmtNum(netTotal)} ₺</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Toplam KDV</span>
                  <span className="text-foreground/80">{fmtNum(totalVat)} ₺</span>
                </div>

                <div className="border-t border-border my-2" />

                <div className="flex justify-between text-base font-semibold" data-testid="invoice-grand-total">
                  <span className="text-foreground">Genel Toplam</span>
                  <span className="text-foreground">{fmtNum(generalTotal)} ₺</span>
                </div>

                {/* Currency conversion note */}
                <div className="mt-3 pt-3 border-t border-border text-xs text-muted-foreground text-right">
                  <div>{normalizeCurrencyLabel(invoice.currency)} Karşılığı</div>
                  <div className="font-medium text-muted-foreground">{fmtNum(generalTotal)} ₺</div>
                </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
}
